import { debounce } from '@theme/utilities';
import { DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

const DESKTOP_MQ = window.matchMedia('(min-width: 750px)');
const SCRIM_HIDDEN_TOP = '100dvh';

/** @type {ResizeObserver | null} */
let resizeObserver = null;

/** @type {boolean} */
let wasDesktop = DESKTOP_MQ.matches;

/**
 * @returns {HTMLElement & { closeDialog?: () => void; refs?: { dialog?: HTMLDialogElement } } | null}
 */
function getSearchModal() {
  const searchModal = document.getElementById('search-modal');
  return searchModal instanceof HTMLElement ? searchModal : null;
}

/**
 * @returns {HTMLElement & { closeActiveSubmenu?: () => void } | null}
 */
function getHeaderMenu() {
  const headerMenu = document.querySelector('header-menu');
  return headerMenu instanceof HTMLElement ? headerMenu : null;
}

/**
 * @param {HTMLDialogElement} dialog
 */
function syncScrimPosition(dialog) {
  if (!DESKTOP_MQ.matches) return;

  const { bottom } = dialog.getBoundingClientRect();
  dialog.style.setProperty('--search-scrim-top', `${bottom}px`);
}

/**
 * @param {HTMLDialogElement} dialog
 */
function resetScrimPosition(dialog) {
  dialog.style.setProperty('--search-scrim-top', SCRIM_HIDDEN_TOP);
}

/**
 * @param {HTMLDialogElement} dialog
 */
function startScrimObserver(dialog) {
  stopScrimObserver();

  resizeObserver = new ResizeObserver(() => {
    syncScrimPosition(dialog);
  });
  resizeObserver.observe(dialog);
}

function stopScrimObserver() {
  resizeObserver?.disconnect();
  resizeObserver = null;
}

/**
 * @param {Event} event
 */
function handleSearchOpen(event) {
  if (!(event.target instanceof HTMLElement) || event.target.id !== 'search-modal') return;
  if (!DESKTOP_MQ.matches) return;

  const searchModal = getSearchModal();
  const dialog = searchModal?.refs?.dialog;
  if (!(dialog instanceof HTMLDialogElement)) return;

  getHeaderMenu()?.closeActiveSubmenu?.();

  requestAnimationFrame(() => {
    syncScrimPosition(dialog);
    startScrimObserver(dialog);
  });
}

/**
 * @param {Event} event
 */
function handleSearchClose(event) {
  if (!(event.target instanceof HTMLElement) || event.target.id !== 'search-modal') return;

  const searchModal = getSearchModal();
  const dialog = searchModal?.refs?.dialog;

  stopScrimObserver();

  if (dialog instanceof HTMLDialogElement) {
    resetScrimPosition(dialog);
  }
}

function handleMegaMenuHover() {
  if (!DESKTOP_MQ.matches) return;

  const searchModal = getSearchModal();
  const dialog = searchModal?.refs?.dialog;

  if (dialog?.open) {
    searchModal?.closeDialog?.();
  }
}

const handleBreakpointChange = debounce(() => {
  const isDesktop = DESKTOP_MQ.matches;
  const searchModal = getSearchModal();
  const dialog = searchModal?.refs?.dialog;

  if (wasDesktop !== isDesktop && dialog?.open) {
    searchModal?.closeDialog?.();
  }

  wasDesktop = isDesktop;
}, 50);

function initSearchModal() {
  const searchModal = getSearchModal();
  if (!searchModal) return;

  searchModal.addEventListener(DialogOpenEvent.eventName, handleSearchOpen);
  searchModal.addEventListener(DialogCloseEvent.eventName, handleSearchClose);
  document.addEventListener(ThemeEvents.megaMenuHover, handleMegaMenuHover);
  window.addEventListener('resize', handleBreakpointChange);
}

initSearchModal();
